import useSWR from 'swr'
import { getApiUrl } from '@/config/api'
import { useAuthToken } from './useAuthToken'
import { useRole } from './useRole'
import { ClerkUserRole } from '@/types/auth'
import { FeatureAccessPath } from '@/types/configuration'

type UseOrgGroupAccessOptions = {
  configPath: FeatureAccessPath
  restrictedRoles?: ClerkUserRole[]
}

type UseOrgGroupAccessReturn = {
  hasAccess: boolean
  isLoading: boolean
  configValue?: any
}

type FeatureAccessResponse = {
  has_access: boolean
  config_value?: any
}

export function useOrgGroupAccess({ configPath, restrictedRoles }: UseOrgGroupAccessOptions): UseOrgGroupAccessReturn {
  const { userRole } = useRole()
  const { getToken, isSignedIn } = useAuthToken()

  // Determine if this is a restricted role
  const isRestrictedRole = restrictedRoles && userRole ? restrictedRoles.includes(userRole) : true

  // Only fetch if:
  // 1. User is signed in
  // 2. User role is in restrictedRoles (or restrictedRoles is undefined)
  // Note: We now fetch even if user has no group, as org-level configs should still apply
  const shouldFetch = isSignedIn && isRestrictedRole

  // Always call useSWR unconditionally (React Hooks rules)
  const { data, isLoading } = useSWR<FeatureAccessResponse>(
    shouldFetch ? `/configurations/feature-access?config_path=${encodeURIComponent(configPath)}` : null,
    async (url: string) => {
      const token = await getToken()
      if (!token) throw new Error('No auth token available')

      const response = await fetch(getApiUrl(url), {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        const errorBody = await response.text().catch(() => 'Unable to read response body')
        console.error('[useOrgGroupAccess] API Error:', {
          status: response.status,
          statusText: response.statusText,
          url,
          body: errorBody,
        })
        throw new Error(`Failed to fetch: ${response.statusText}`)
      }

      const result = await response.json()
      return result
    },
    {
      revalidateOnFocus: false,
      dedupingInterval: 5 * 60 * 1000, // 5 minutes - configs don't change frequently
    }
  )

  // If not a restricted role, grant access immediately
  if (!isRestrictedRole) {
    return { hasAccess: true, isLoading: false }
  }

  // If still loading, return loading state with no access
  if (isLoading) {
    return { hasAccess: false, isLoading: true }
  }

  // Return the actual response from the API
  // Users without a group will get org-level configuration
  return {
    hasAccess: data?.has_access ?? false,
    isLoading: false,
    configValue: data?.config_value,
  }
}
